import { Button } from '@mui/material'
import { useCallback } from 'react'
import { SceneManager } from '../../../visualization/sceneManager'
import { useDispatch, useSelector } from 'react-redux'
import {
  isDoorMesh,
  isStructuralMesh,
  isWallMesh,
  isWindowMesh,
} from '../../../visualization/utils'
import {
  removeDoor,
  removeStructuralElement,
  removeWall,
  removeWindow,
} from '../../../actions/construction'
import store, { RootState } from '../../../store'
import { updateHistory } from '../../../actions/history'
import { ActionType } from '../../../reducers/history'
import { EventType } from '../../../utils/EventEmitter'
import { createSelector } from '@reduxjs/toolkit'
import { ConstructionTab } from '../../../reducers/construction'
import { BuildingEditingTool } from '../../../models/Construction'
import { validate as isValidUUID } from 'uuid'

const selectSelectedElementMesh = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (constructionTab, tool, elementsIds) => {
    const elementId = elementsIds[0]
    if (
      constructionTab !== ConstructionTab.Editor ||
      tool !== BuildingEditingTool.DirectSelect ||
      !elementId ||
      !isValidUUID(elementId)
    ) {
      return null
    }

    const scene = SceneManager.getInstance().getScene()
    const mesh = scene?.getMeshById(elementId)
    const allowedMeshTypes = [
      isWallMesh,
      isWindowMesh,
      isDoorMesh,
      isStructuralMesh,
    ]

    if (mesh && allowedMeshTypes.some((fn) => fn(mesh))) {
      return mesh
    }

    return null
  },
)

const ObjectDeleteButton = () => {
  const dispatch = useDispatch<any>()
  const elementToDelete = useSelector(selectSelectedElementMesh)

  const handleDeleteButtonClick = useCallback(() => {
    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    if (!elementToDelete || !eventEmitter) {
      return
    }

    if (isWallMesh(elementToDelete)) {
      dispatch(removeWall({ wallId: elementToDelete.id }))
    } else if (isWindowMesh(elementToDelete)) {
      dispatch(removeWindow({ windowId: elementToDelete.id }))
    } else if (isDoorMesh(elementToDelete)) {
      dispatch(removeDoor({ doorId: elementToDelete.id }))
    } else if (
      isStructuralMesh(elementToDelete) &&
      elementToDelete.metadata.structuralType
    ) {
      dispatch(
        removeStructuralElement({
          elementId: elementToDelete.id,
          structuralType: elementToDelete.metadata.structuralType,
        }),
      )
    }

    const convertData = structuredClone(
      store.getState().construction.convertData,
    )
    dispatch(
      updateHistory({
        convertData,
        items: [
          {
            itemId: elementToDelete.id,
            actionType: ActionType.DELETE,
          },
        ],
      }),
    )

    eventEmitter.emit(EventType.BuildingRemoveElement, elementToDelete)
  }, [elementToDelete, dispatch])

  if (!elementToDelete) {
    return null
  }

  const component = (
    <Button
      disableRipple
      size='small'
      onClick={handleDeleteButtonClick}
      sx={{
        textTransform: 'none',
        background: '#FF3B47',
        color: '#ffffff',
        fontSize: '0.76rem',
        width: '100%',
      }}
    >
      Delete
    </Button>
  )

  return {
    component,
    meshes: [elementToDelete],
  }
}

export default ObjectDeleteButton
